/**
 * Runtime ZwConfig types.
 *
 * On-disk `.zwconfig` shape is generated from `schemas/zwconfig.schema.json`
 * (`./generated/onDisk`). This module adds UI label tables, manuscript synopsis
 * fields (frontmatter-only at rest), and `.zwlocal` overlays merged into the
 * frontend API payload.
 */
export type {
  ZwChapterNumbering,
  OnDiskManuscriptItem,
  OnDiskZwAct,
  OnDiskZwChapter,
  OnDiskZwConfig,
  OnDiskZwScene,
  ZwCopyrightCredit,
  ZwCopyrightPage,
  ZwDatabaseFieldDef,
  ZwDatabaseFieldKind,
  ZwDatabaseRelationPair,
  ZwDedicationPage,
  ZwFonts,
  ZwGoals,
  ZwMetadata,
  ZwChapterHeadingStyle,
  ZwChapterTitleAlignment,
  ZwChapterTitleImagePosition,
  ZwPageChrome,
  ZwPageNumberPlacement,
  ZwFirstParagraph,
  ZwOpeningInitial,
  ZwOpeningLeadIn,
  ZwParagraphFormatting,
  ZwParagraphStyle,
  ZwPrintSize,
  ZwSceneBreakStyle,
  ZwSceneFormatting,
  ZwStatusBar,
} from "./generated/onDisk";

export type {
  ZwCloudDivergence,
  ZwCloudDivergenceChoice,
  ZwCloudSync,
} from "../platform/generated/zwLocal";

import type {
  OnDiskZwAct,
  OnDiskZwChapter,
  OnDiskZwConfig,
  OnDiskZwScene,
  ZwDatabaseFieldKind,
  ZwChapterTitleAlignment,
  ZwChapterTitleImagePosition,
  ZwFirstParagraph,
  ZwOpeningInitial,
  ZwOpeningLeadIn,
  ZwPageNumberPlacement,
  ZwParagraphStyle,
  ZwPrintSize,
  ZwSceneBreakStyle,
} from "./generated/onDisk";
import type { ZwCloudSync } from "../platform/generated/zwLocal";

/** Clamps for chapter heading spacing (em) and title scale. */
export const CHAPTER_HEADING_SPACE_MIN = 0;
export const CHAPTER_HEADING_SPACE_MAX = 6;
export const CHAPTER_HEADING_SCALE_MIN = 0.5;
export const CHAPTER_HEADING_SCALE_MAX = 2;

/** Where page numbers appear on body pages (not title / chapter openers). */
export const PAGE_NUMBER_PLACEMENTS = [
  { id: "header", label: "Header" },
  { id: "footer", label: "Footer" },
  { id: "none", label: "None" },
] as const satisfies ReadonlyArray<{
  id: ZwPageNumberPlacement;
  label: string;
}>;

export function isZwPageNumberPlacement(
  value: string,
): value is ZwPageNumberPlacement {
  return PAGE_NUMBER_PLACEMENTS.some((item) => item.id === value);
}

/** What separates concatenated scenes within a chapter on export, when scene
 * titles are off (or in addition to them). */
export const SCENE_BREAK_STYLES = [
  { id: "none", label: "None (paragraph break only)" },
  { id: "blank", label: "Blank line" },
  { id: "asterisk", label: "Asterisk (* * *)" },
  { id: "hash", label: "Hash (#)" },
  { id: "custom", label: "Custom text" },
] as const satisfies ReadonlyArray<{
  id: ZwSceneBreakStyle;
  label: string;
}>;

export function isZwSceneBreakStyle(value: string): value is ZwSceneBreakStyle {
  return SCENE_BREAK_STYLES.some((item) => item.id === value);
}

export const PRINT_SIZES = [
  { id: "a1", label: "A1 (594 × 841 mm)" },
  { id: "a2", label: "A2 (420 × 594 mm)" },
  { id: "a3", label: "A3 (297 × 420 mm)" },
  { id: "a4", label: "A4 (210 × 297 mm)" },
  { id: "a5", label: "A5 (148 × 210 mm)" },
  { id: "letter", label: "US Letter (8.5 × 11 in)" },
  { id: "legal", label: "US Legal (8.5 × 14 in)" },
  { id: "tabloid", label: "Tabloid (11 × 17 in)" },
  { id: "trade", label: "Trade (6 × 9 in)" },
  { id: "digest", label: "Digest (5.5 × 8.5 in)" },
] as const satisfies ReadonlyArray<{ id: ZwPrintSize; label: string }>;

export const DEFAULT_PRINT_SIZE: ZwPrintSize = "a5";

export function isZwPrintSize(value: string): value is ZwPrintSize {
  return PRINT_SIZES.some((size) => size.id === value);
}

/** Styling ▸ Paragraphs: how paragraphs are set in the editor and books. */
export const PARAGRAPH_STYLES = [
  { id: "indented", label: "Indented first line, no space between" },
  { id: "spaced", label: "Space between paragraphs, no indent" },
] as const satisfies ReadonlyArray<{ id: ZwParagraphStyle; label: string }>;

export function isZwParagraphStyle(value: string): value is ZwParagraphStyle {
  return PARAGRAPH_STYLES.some((style) => style.id === value);
}

/** With indented paragraphs: how a chapter's or scene's first is set. */
export const FIRST_PARAGRAPHS = [
  { id: "indented", label: "Same indentation as other paragraphs" },
  { id: "chapterFlush", label: "First paragraph in chapter flush left" },
  { id: "sceneFlush", label: "First paragraph of every scene flush left" },
] as const satisfies ReadonlyArray<{ id: ZwFirstParagraph; label: string }>;

export function isZwFirstParagraph(value: string): value is ZwFirstParagraph {
  return FIRST_PARAGRAPHS.some((item) => item.id === value);
}

/** First letter of each chapter's first paragraph. */
export const OPENING_INITIALS = [
  { id: "none", label: "None" },
  { id: "dropCap", label: "Drop cap" },
  { id: "raisedInitial", label: "Raised initial" },
] as const satisfies ReadonlyArray<{ id: ZwOpeningInitial; label: string }>;

export function isZwOpeningInitial(value: string): value is ZwOpeningInitial {
  return OPENING_INITIALS.some((item) => item.id === value);
}

/** Small-caps lead-in on each chapter's first paragraph. */
export const OPENING_LEAD_INS = [
  { id: "none", label: "None" },
  { id: "words", label: "First few words" },
  { id: "sentence", label: "First sentence" },
] as const satisfies ReadonlyArray<{ id: ZwOpeningLeadIn; label: string }>;

export function isZwOpeningLeadIn(value: string): value is ZwOpeningLeadIn {
  return OPENING_LEAD_INS.some((item) => item.id === value);
}

/** Styling ▸ Chapter titles: alignment of the injected chapter title. */
export const CHAPTER_TITLE_ALIGNMENTS = [
  { id: "left", label: "Left" },
  { id: "center", label: "Center" },
  { id: "right", label: "Right" },
] as const satisfies ReadonlyArray<{
  id: ZwChapterTitleAlignment;
  label: string;
}>;

export function isZwChapterTitleAlignment(
  value: string,
): value is ZwChapterTitleAlignment {
  return CHAPTER_TITLE_ALIGNMENTS.some((item) => item.id === value);
}

/** Where an optional chapter title image sits relative to the title text. */
export const CHAPTER_TITLE_IMAGE_POSITIONS = [
  { id: "above", label: "Above the title" },
  { id: "below", label: "Below the title" },
] as const satisfies ReadonlyArray<{
  id: ZwChapterTitleImagePosition;
  label: string;
}>;

export function isZwChapterTitleImagePosition(
  value: string,
): value is ZwChapterTitleImagePosition {
  return CHAPTER_TITLE_IMAGE_POSITIONS.some((item) => item.id === value);
}

/** Bibliographic fields for export (EPUB, PDF, DOCX, etc.). */
export const PROJECT_ASSETS_DIR = "Assets";
export const PROJECT_TEMPLATES_DIR = "Templates";
export const PROJECT_NOTES_DIR = "Notes";
export const PROJECT_DATABASE_DIR = "Database";
export const PROJECT_CHAPTERS_DIR = "Chapters";

/**
 * A single scene file within a chapter folder. The display name is derived from
 * `file` itself (an ordinal "Scene N" while it's still the auto-generated
 * `scene-{suffix}.md`, else the filename), same model as chapters, where the
 * folder is a stable identifier.
 *
 * `synopsis` is API/runtime only: read from the scene file's frontmatter and
 * never persisted into `.zwconfig`.
 */
export type ZwScene = OnDiskZwScene & {
  synopsis: string;
};

/**
 * One manuscript chapter: `Chapters/<folder>/index.zwdoc` (metadata/synopsis,
 * not exported) plus one or more scene files (the exported prose).
 * `scenes` is never empty. A lone scene renders as today's flat chapter row.
 *
 * `name` and `synopsis` are API/runtime only: read from the chapter index header and never
 * persisted into `.zwconfig`.
 */
export type ZwChapter = Omit<OnDiskZwChapter, "scenes"> & {
  name: string;
  scenes: ZwScene[];
  synopsis: string;
};

/** Metadata-only chapter grouping in .zwconfig, with a stable identity and name. */
export type ZwAct = Omit<OnDiskZwAct, "chapters" | "index"> & {
  /** Legacy UI identity only; never an act document. */
  index: string;
  name: string;
  chapters: ZwChapter[];
};

/**
 * A map figure leaf in the manuscript outline (#266). Points at a worldbuilding
 * `.zwmap`; does not own the map file. Placement options control book layout.
 */
export type ZwMapFigure = {
  id: string;
  title: string;
  /** Project-relative path, usually `Database/<name>.zwmap`. */
  mapPath: string;
  /** Clockwise rotation in degrees for book placement. */
  orientation: 0 | 90 | 180 | 270;
  /** `single` = one page; `spread` = one map across two facing pages. */
  spread: "single" | "spread";
  /**
   * How far the figure fills the page (Vellum-style).
   * Maps default to `safeZone`, not text margins.
   */
  printExtent: "textMargins" | "safeZone" | "fullBleed";
  /**
   * How the map scales inside the print slot.
   * `contain` letterboxes; `cover` fills and may crop.
   */
  objectFit: "contain" | "cover";
};

/**
 * One entry in the manuscript order: an ungrouped chapter, an act grouping
 * chapters beneath it, or a map figure leaf. Acts, ungrouped chapters, and maps
 * interleave freely.
 *
 * Anything that walks "every chapter in order" (export, word count, scene file
 * lists, active-path reconcile) should go through `flattenChapters` and stay
 * act-agnostic; only the outline, Storyboard, and drag-and-drop use the tree.
 */
export type ManuscriptItem =
  | ({ type: "chapter" } & ZwChapter)
  | ({ type: "act" } & ZwAct)
  | ({ type: "map" } & ZwMapFigure);

/**
 * Shared project settings live in `.zwconfig` (committed / cloud-backed).
 * Device-only sync cursors + lastRoute + collapsed folders live in `.zwlocal`
 * (never committed). Sticky open files are Pinia session memory only.
 * `.zw/` remains ephemeral scratch only (temp bundles during export/import).
 *
 * The frontend still sees `cloudSync` on `ZwConfig`. Rust merges it from
 * `.zwlocal` when reading. Writing-goal baselines are computed from git in
 * memory, not stored on disk.
 *
 * Overlay field shapes (`ZwCloudSync`) are defined in
 * `schemas/zwlocal.schema.json` and re-exported from the generated module.
 */

/** Device-local fields merged into the frontend `ZwConfig` API payload. */
export type ZwConfigLocalOverlay = {
  /** Device-local sync cursors (from `.zwlocal`). */
  /** Absent when no device-local sync cursors are stored. */
  cloudSync?: ZwCloudSync;
};

/** The kind of value a custom worldbuilding field (#72) holds. Mirrors the
 * shapes the built-in fields already use (`role`/`status` are short text,
 * `summary` is long text, `aka`/`tags` are string lists) plus an
 * author-defined `select`. */
export const DATABASE_FIELD_KINDS = [
  { id: "text", label: "Short text" },
  { id: "longText", label: "Long text" },
  { id: "tags", label: "Tag list" },
  { id: "select", label: "Select (choose one)" },
] as const satisfies ReadonlyArray<{
  id: ZwDatabaseFieldKind;
  label: string;
}>;

export function isZwDatabaseFieldKind(
  value: string,
): value is ZwDatabaseFieldKind {
  return DATABASE_FIELD_KINDS.some((k) => k.id === value);
}

export function newDatabaseFieldId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `field-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function newDatabaseRelationPairId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `rel_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `rel_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Runtime project config: on-disk `.zwconfig` shape plus manuscript synopsis
 * fields and `.zwlocal` overlays.
 */
export type ZwConfig = Omit<OnDiskZwConfig, "manuscript"> & {
  manuscript: ManuscriptItem[];
  /** API-only: reloaded from each chapter index, never written to .zwconfig. */
  chapterNumberingOverrides: Record<
    string,
    import("../chapterNumbering").ChapterNumberingOverride
  >;
} & ZwConfigLocalOverlay;
